import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import './Tabs.scss';
import { TabsList } from './TabsList';
import { TabItem } from './TabItem';
import { TabsContent } from './TabsContent';
import { ERouterPath } from '../router/ERouterPath';
import { Icon } from '../icon/Icon';
import { TabsHeader } from './TabsHeader';
import { useSelector } from 'react-redux';
import { DossierMemberInterface } from '../../models/DossierMember';
import { ApplicationState } from '../../app/store';
import { SingleUser, UserAccountRoleEnum, UserRelationRoleEnum } from '../../api/types';
import { unreadState$ } from '../../api';
import { useHistory, useRouteMatch } from 'react-router';

interface ITabsProps {
  fullScreenMobile?: boolean;
  showTabsInitially?: boolean;
  grayBackgroundMobile?: boolean;
}

interface ITabItem {
  title: string;
  className: string;
  redirectTo: ERouterPath | string;
  matchPaths: ERouterPath[];
  badge?: number;
}

const tabItemReports: ITabItem = { title: 'Verslagen', className: 'tabs__item--client-dossier', redirectTo: ERouterPath.reports, matchPaths: [ERouterPath.reports, ERouterPath.reportsClient, ERouterPath.reportsResponsible, ERouterPath.reportsShared] };
const tabItemAgenda: ITabItem = { title: 'Afspraken', className: 'tabs__item--agenda', redirectTo: ERouterPath.appointments, matchPaths: [ERouterPath.appointments] };
const tabItemDocuments: ITabItem = { title: 'Materialen', className: 'tabs__item--documents', redirectTo: ERouterPath.documents, matchPaths: [ERouterPath.documents, ERouterPath.documentsOfUser] };
const tabItemsLibrary: ITabItem = { title: 'Oefeningen', className: 'tabs__item--library', redirectTo: ERouterPath.library, matchPaths: [ERouterPath.library, ERouterPath.libraryItemsForRelation] };
const tabItemMeasurements: ITabItem = { title: 'Meters', className: 'tabs__item--measurements', redirectTo: ERouterPath.tools, matchPaths: [ERouterPath.tools, ERouterPath.measurement, ERouterPath.measurementNew, ERouterPath.measurementUnanswered, ERouterPath.measurementOverview] };

export function Tabs({ fullScreenMobile, showTabsInitially, grayBackgroundMobile, children }: PropsWithChildren<ITabsProps>): JSX.Element {
  const [showTabs, setShowTabs] = useState(showTabsInitially ?? false); // For mobile only, tabs are always visible on tablet and desktop
  const history = useHistory();
  const match = useRouteMatch();
  const [tabItems, setTabItems] = useState<ITabItem[]>([]);
  const [unreadChatsCount, setUnreadChatsCount] = useState<number>(0);
  const dossierRelation = useSelector<ApplicationState, DossierMemberInterface|undefined>(state => state.session.relation);
  const user = useSelector<ApplicationState, SingleUser|undefined>(state => state.session.user);

  const tabItemChat = useMemo<ITabItem>(() => ({ title: 'Chat', className: 'tabs__item--chat', redirectTo: ERouterPath.chat, matchPaths: [ERouterPath.chat, ERouterPath.chatConversation], badge: unreadChatsCount }), [unreadChatsCount]);

  useEffect(() => {
    const subscription = unreadState$.subscribe(unread => {
      setUnreadChatsCount(unread.total);
      return () => subscription.unsubscribe();
    });
  }, []);


  useEffect(() => {
    let visibleTabItems: ITabItem[] = [];
    if (!dossierRelation?.role && user && [UserAccountRoleEnum.adminCaregiver, UserAccountRoleEnum.administration].includes(user.accountRole)) {
      // If user has no role in dossier, but is admin
      visibleTabItems = [tabItemReports, tabItemChat, tabItemAgenda];
    } else if (dossierRelation?.role) {
      // User with any role in dossier
      switch (dossierRelation.role) {
        case UserRelationRoleEnum.client:
        case UserRelationRoleEnum.responsibleParty:
        case UserRelationRoleEnum.caregiver:
          visibleTabItems = [tabItemReports, tabItemChat, tabItemAgenda, tabItemDocuments, tabItemsLibrary, tabItemMeasurements];
          break;
        default:
          visibleTabItems = [tabItemChat, tabItemAgenda, tabItemDocuments, tabItemsLibrary, tabItemMeasurements];
          break;
      }
    }
    setTabItems(visibleTabItems);
  }, [dossierRelation, user, tabItemChat]);

  const handleClick = (goTo: ERouterPath | string) => {
    setShowTabs(false);
    history.push(goTo);
  };

  const renderTabItems = (): JSX.Element[] => tabItems.map((item, index) => {
    const { title, className, badge, redirectTo, matchPaths } = item;
    const isActive = matchPaths.includes(match.path as ERouterPath);
    return <TabItem key={index} title={title} className={className} badge={badge} isActive={isActive} onClick={() => handleClick(redirectTo)} />;
  });

  return <>
    <TabsHeader />
    <div className="tabs">
      <TabsList>
        {renderTabItems()}
      </TabsList>
      <TabsContent isActive={!showTabs} fullScreenMobile={fullScreenMobile} grayBackgroundMobile={grayBackgroundMobile}>
        {children}
        { !fullScreenMobile && <div className="mobile-home-button__placeholder" /> }
      </TabsContent>
    </div>
    { !showTabs && !fullScreenMobile && <button className="mobile-home-button__button" onClick={() => setShowTabs(true)} title="Home"><Icon className="mobile-home-button__button-icon" icon="home" /></button>}
  </>;
}
